import styled, { css } from 'styled-components';
import { useEffect } from 'react';

import { IRobotAsset } from 'shared/types/RobotAsset';
import { isCurrentRobotImageExpanded } from 'productTemplate/utils/isCurrentRobotExpanded';
import { RobotImage } from 'productTemplate/components/robotGallery/RobotImage';
import { RobotExpandedView } from 'productTemplate/components/robotGallery/RobotExpandedView';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useStoreSelector } from 'store/hooks/storeHooks';

interface IRobotGalleryProps {
  expandedImagesUrls: IRobotAsset[];
  robotList: IRobotAsset[];
  setExpandedImagesUrls: (val) => void;
  setZoomedInRobot: (val) => void;
  zoomedInRobot: string;
}

export const RobotGallery = ({
  robotList,
  expandedImagesUrls,
  setExpandedImagesUrls,
  setZoomedInRobot,
  zoomedInRobot,
}: IRobotGalleryProps) => {
  const { slug, name } = useStoreSelector((state) => state.pdp.productTemplate);
  const isExpanded = expandedImagesUrls.length > 0;

  const handleClick = (robot: IRobotAsset, index: number) => {
    const robotUrl = robot.url;
    const isCurrentRobotUrlExpanded = isCurrentRobotImageExpanded(expandedImagesUrls, robotUrl);

    sendTrackingEvent('PDP_MODEL_TAP', {
      product_template_slug: slug,
      picture_url: robotUrl,
      action: isCurrentRobotUrlExpanded ? 'remove' : 'expand',
      model_position: index,
    });

    if (zoomedInRobot === robotUrl) {
      setZoomedInRobot(null);
    }

    return isCurrentRobotUrlExpanded
      ? setExpandedImagesUrls(expandedImagesUrls.filter((image) => image.url !== robotUrl))
      : setExpandedImagesUrls((prevImages) => [robot, ...prevImages]);
  };

  const handleClose = () => {
    if (isExpanded) {
      sendTrackingEvent('PDP_MODEL_EXIT', {
        product_template_slug: slug,
      });

      setExpandedImagesUrls([]);
    }
  };

  const handleHover = () => {
    sendTrackingEvent('PDP_MODEL_HOVER', {
      product_template_slug: slug,
    });
  };

  useEffect(() => {
    setExpandedImagesUrls([]);
  }, [slug]);

  return (
    <RobotGallery.Wrapper
      data-qa="robot_gallery_desktop"
      isExpanded={isExpanded}
      onClick={handleClose}
    >
      <RobotGallery.DefaultView
        data-qa="robot_default_view"
        isExpanded={isExpanded}
        onMouseEnter={handleHover}
        onClick={(e) => e.stopPropagation()}
      >
        {robotList.map((robot, idx) => (
          <RobotImage
            key={robot.url}
            handleClick={() => handleClick(robot, idx)}
            isSelected={isCurrentRobotImageExpanded(expandedImagesUrls, robot.url)}
            productName={name}
            url={robot.url}
          />
        ))}
      </RobotGallery.DefaultView>
      {isExpanded && (
        <RobotGallery.ExpandedView onClick={(e) => e.stopPropagation()}>
          <RobotExpandedView
            expandedImagesUrls={expandedImagesUrls}
            productName={name}
            setZoomedInRobot={setZoomedInRobot}
            zoomedInRobot={zoomedInRobot}
          />
        </RobotGallery.ExpandedView>
      )}
    </RobotGallery.Wrapper>
  );
};

RobotGallery.Wrapper = styled.div<{
  isExpanded: boolean;
}>`
  max-height: 100vh;
  position: absolute;
  width: 100%;
  z-index: ${({ theme }) => theme.zIndex.robotImage};
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ isExpanded }) =>
    isExpanded &&
    css`
      height: calc(100vh - 166px);
      overflow: hidden;

      &:hover {
        cursor: url('/images/cursors/close.svg'), auto !important;
      }
    `}
`;

RobotGallery.DefaultView = styled.div<{
  isExpanded: boolean;
}>`
  height: 160px;
  max-width: 100%;
  align-self: flex-start;
  display: flex;
  flex-direction: row;
  position: absolute;
  left: 32px;
  top: 32px;
  z-index: ${({ theme }) => theme.zIndex.pdpFooter};

  &:hover {
    cursor: pointer;
  }

  > div:not(:first-child) {
    margin-left: -30px;
  }

  &:hover > div:not(:first-child) {
    margin-left: 8px;
  }

  ${({ isExpanded }) =>
    isExpanded &&
    css`
      > div:not(:first-child) {
        margin-left: 8px;
      }
    `}
`;

RobotGallery.ExpandedView = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;

  &:hover {
    cursor: default;
  }

  @media (max-height: 750px) {
    height: calc(100% - 50px);
  }
`;

RobotGallery.Close = styled.button`
  cursor: pointer;
  padding: 0;
  align-items: center;
`;
